import { AppBar, Autocomplete, Box, Button, Card, CardActions, CardHeader, Dialog, FormControl, Unstable_Grid2 as Grid, IconButton, InputLabel, MenuItem, Select, Stack, SvgIcon, TextField, Typography } from "@mui/material";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { ObjectID } from "bson";
import { pick } from "lodash";
import { useMemo } from "react";
import currencyMap from "@dcspark/currency-symbol-map/map";
import { FixDiscountInput, FileInputLike, FileProductInputLike, FileProductOptionInputLike, FixDiscountLike, AdditionalFeeInput, AdditionalFeeLike } from "~served/utils/calculations/types";
import { ProductsTableSummary, TableItem, TableItemDisplay, TableSubtotalAddons } from "~/components/products-table-summary";
import { useAppRouter } from "~/hooks/use-app-router";
import { useTillSelectedCategory } from "~/hooks/use-till-selected-category";
import { useTillSummary } from "~/hooks/use-till-summary";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { GetTillProductCategoriesResults, TillProduct, useGetTillProductCategories } from "~/queries/useGetTillProductCategories";
import { UpdateFileVariables, useUpdateFile } from "~/queries/useUpdateFile";
import { useSetTillIsEditingFileInput, useSetTillIsOpen, useSetTillSelectedCategoryId, useTill, useUpdateTillCreateFileInput, useUpdateTillFileInputSnapshot, useUpdateTillTempProductInput, useUpdateTillUpdateFileInput } from "~/store";
import { DISCOUNT_TYPE, FileProductInput, FixDiscount, FIX_DISCOUNT_TYPE, CreateFileInput, AdditionalFee, FileProductOptionInput } from "~/types/__generated/gql/graphql";
import { TempProductModal } from "./temp-product-modal";
import { GetFixDiscountsResults, useGetFixDiscounts } from "~/queries/useGetFixDiscounts";
import { applyAdditionalFeeToFile, applyAdditionalFeeToProduct, applyFixDiscountToFile, applyFixDiscountToProduct, applyNonOrderAdditionalFeeToFile, applyNonOrderFixDiscountToFile, bigMath, calculateFile, isOrderFixDiscount } from "~served/utils";
import { GetAdditionalFeesResults, useGetAdditionalFees } from "~/queries/useGetAdditionalFees";
import { isOrderAdditionalFee } from "~served/utils/calculations/additional-fee";
import { useGetCurrentCompany } from "~/queries/useGetCurrentCompany";
import { DEFAULT_CURRENCY_CODE } from "~served/configs";
import { FileProductMultiCurrencyConfig } from "~served/types/db";
import { $dTillProductPrice } from "~/utils/multi-currency";
export const TillModal = () => {
  const {
    selectedCategoryId,
    selectedProductId,
    isEditingFileInput,
    createFileInput,
    updateFileInput,
    fileInputSnapshot
  } = useTill();
  const setTillIsOpen = useSetTillIsOpen();
  const setSelectCategoryId = useSetTillSelectedCategoryId();
  const updateCreateFileInput = useUpdateTillCreateFileInput();
  const updateTempProduct = useUpdateTillTempProductInput();
  const setTillIsEditingFileInput = useSetTillIsEditingFileInput();
  const {
    appQuery: {
      fileId
    },
    currentActions: {
      isViewingFile,
      isCreatingFile
    }
  } = useAppRouter();
  const {
    createFileInputSummary,
    updateFileInputSummary,
    tillItemsSummary
  } = useTillSummary();
  const {
    data: company
  } = useGetCurrentCompany();
  const {
    data: categories = []
  } = useGetTillProductCategories();
  const selectedCategory = useTillSelectedCategory();
  const updateFile = useUpdateFile();
  const updateUpdateFileInput = useUpdateTillUpdateFileInput();
  const setTillIsEditFileInput = useSetTillIsEditingFileInput();
  const updateTillFileInputSnapshot = useUpdateTillFileInputSnapshot();
  const handleClose = async () => {
    if (isCreatingFile) {
      updateTillFileInputSnapshot(createFileInput);
    }
    if (isViewingFile) {
      // handle added product seperated from prev to keep edit history
      const updatedProducts = updateFileInputSummary.products.map(p => {
        const matchAdded = tillItemsSummary.add.products.find(a => p._id === a._id);
        if (matchAdded) {
          return {
            ...p,
            quantity: p.quantity - matchAdded.quantity
          };
        } else {
          return p;
        }
      }).concat(tillItemsSummary.add.products.map(p => ({
        ...p,
        _id: new ObjectID().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })));
      void updateTillFileInputSnapshot(updateFileInput);
      await updateFile.mutateAsync({
        id: fileId!,
        input: {
          products: updatedProducts as UpdateFileVariables["input"]["products"],
          ...(updateFileInput?.multiCurrencyConfig ? {
            multiCurrencyConfig: updateFileInput.multiCurrencyConfig
          } : undefined)
        }
      });
    }
    setTillIsEditFileInput(false);
    setTillIsOpen(false);
  };
  const handleCancel = () => {
    if (isCreatingFile) {
      updateCreateFileInput({
        ...createFileInput,
        products: fileInputSnapshot?.products ?? [],
        cancelledProducts: fileInputSnapshot?.cancelledProducts ?? []
      });
    }
    if (isViewingFile) {
      updateUpdateFileInput(fileInputSnapshot);
    }
    setTillIsEditFileInput(false);
    setTillIsOpen(false);
  };
  const addProductToCreateInput = (product: TillProduct) => {
    const {
      $isAllProductsWithFixDiscount,
      $isAllProductsWithAdditionalFee,
      products
    } = calculateFile(createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>);
    const tmpDiscount = $isAllProductsWithFixDiscount ? {
      ...(products[0].subtotalAddons.fixDiscount.fixDiscount ? {
        fixDiscount: products[0].subtotalAddons.fixDiscount.fixDiscount
      } : undefined),
      type: products[0].subtotalAddons.fixDiscount.type,
      value: products[0].subtotalAddons.fixDiscount.value
    } as unknown as FixDiscountInput : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    };
    const tmpFee = $isAllProductsWithAdditionalFee ? {
      ...(products[0].subtotalAddons.additionalFee.additionalFee ? {
        additionalFee: products[0].subtotalAddons.additionalFee.additionalFee
      } : undefined),
      type: products[0].subtotalAddons.additionalFee.type,
      value: products[0].subtotalAddons.additionalFee.value
    } as unknown as AdditionalFeeInput : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    };
    const isAlreadyAdded = createFileInput!.products.some(p => p.product === product._id);
    if (isAlreadyAdded) {
      updateCreateFileInput(calculateFile({
        ...createFileInput!,
        products: [...createFileInput!.products.map(p => p.product === product._id ? applyAdditionalFeeToProduct({
          product: applyFixDiscountToProduct({
            product: {
              ...p,
              quantity: p.quantity + 1
            } as unknown as FileProductInputLike,
            discount: tmpDiscount
          }),
          fee: tmpFee
        }) : p)]
      } as unknown as FileInputLike).$file as unknown as CreateFileInput);
    } else {
      const newProduct: FileProductInputLike = {
        ...pick(product, ["noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category"]),
        _id: new ObjectID().toString(),
        product: product._id,
        quantity: 1,
        options: [],
        ...(createFileInput?.multiCurrencyConfig ? {
          multiCurrencyConfig: {
            selectedCurrency: createFileInput.multiCurrencyConfig.selectedCurrency,
            priceConfigs: product.priceConfigs ? product.priceConfigs : createFileInput.multiCurrencyConfig.currencyConfigs.map(c => ({
              ...c,
              price: bigMath.mul(product.listedPrice, c.rate)
            }))
          } as FileProductMultiCurrencyConfig
        } : undefined)
      };
      updateCreateFileInput(calculateFile({
        ...createFileInput!,
        products: [...createFileInput!.products, applyAdditionalFeeToProduct({
          product: applyFixDiscountToProduct({
            product: newProduct,
            discount: tmpDiscount
          }),
          fee: tmpFee
        })]
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
  };
  const addProductToUpdateInput = (product: TillProduct) => {
    const {
      $isAllProductsWithFixDiscount,
      $isAllProductsWithAdditionalFee,
      products
    } = calculateFile(updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>);
    const tmpDiscount = $isAllProductsWithFixDiscount ? {
      ...(products[0].subtotalAddons.fixDiscount.fixDiscount ? {
        fixDiscount: products[0].subtotalAddons.fixDiscount.fixDiscount
      } : undefined),
      type: products[0].subtotalAddons.fixDiscount.type,
      value: products[0].subtotalAddons.fixDiscount.value
    } as unknown as FixDiscountInput : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    };
    const tmpFee = $isAllProductsWithAdditionalFee ? {
      ...(products[0].subtotalAddons.additionalFee.additionalFee ? {
        additionalFee: products[0].subtotalAddons.additionalFee.additionalFee
      } : undefined),
      type: products[0].subtotalAddons.additionalFee.type,
      value: products[0].subtotalAddons.additionalFee.value
    } as unknown as AdditionalFeeInput : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    };
    const matchedProduct = tillItemsSummary.add.products.find(p => p.product === product._id);
    if (matchedProduct) {
      updateUpdateFileInput(calculateFile({
        ...updateFileInput!,
        products: [...updateFileInput!.products.map(p => p._id === matchedProduct._id ? applyAdditionalFeeToProduct({
          product: applyFixDiscountToProduct({
            product: {
              ...p,
              quantity: p.quantity + 1
            } as unknown as FileProductInputLike,
            discount: tmpDiscount
          }),
          fee: tmpFee
        }) : p)]
      } as unknown as FileInputLike).$file as unknown as CreateFileInput);
    } else {
      const newProduct: FileProductInputLike = {
        ...pick(product, ["noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category"]),
        _id: new ObjectID().toString(),
        product: product._id,
        quantity: 1,
        options: [],
        ...(updateFileInput?.multiCurrencyConfig ? {
          multiCurrencyConfig: {
            selectedCurrency: updateFileInput.multiCurrencyConfig.selectedCurrency,
            priceConfigs: product.priceConfigs ? product.priceConfigs : updateFileInput.multiCurrencyConfig.currencyConfigs.map(c => ({
              ...c,
              price: bigMath.mul(product.listedPrice, c.rate)
            }))
          } as FileProductMultiCurrencyConfig
        } : undefined)
      };
      updateUpdateFileInput(calculateFile({
        ...updateFileInput!,
        products: [...updateFileInput!.products, applyAdditionalFeeToProduct({
          product: applyFixDiscountToProduct({
            product: newProduct,
            discount: tmpDiscount
          }),
          fee: tmpFee
        })]
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
  };
  const addProductToInput = (product: TillProduct) => {
    if (isCreatingFile) {
      addProductToCreateInput(product);
      setTillIsEditFileInput(true);
    }
    if (isViewingFile) {
      addProductToUpdateInput(product);
      setTillIsEditFileInput(true);
    }
  };
  const addProductToTempProduct = (product: TillProduct) => {
    if (isCreatingFile) {
      updateTempProduct({
        ...pick(product, ["noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category"]),
        _id: new ObjectID().toString(),
        product: product._id,
        quantity: 1,
        options: [],
        ...(createFileInput?.multiCurrencyConfig ? {
          multiCurrencyConfig: {
            selectedCurrency: createFileInput.multiCurrencyConfig.selectedCurrency,
            priceConfigs: product.priceConfigs
          } as FileProductMultiCurrencyConfig
        } as FileProductInput : undefined)
      });
      setTillIsEditFileInput(true);
    }
    if (isViewingFile) {
      updateTempProduct({
        ...pick(product, ["noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category"]),
        _id: new ObjectID().toString(),
        product: product._id,
        quantity: 1,
        options: [],
        ...(updateFileInput?.multiCurrencyConfig ? {
          multiCurrencyConfig: {
            selectedCurrency: updateFileInput.multiCurrencyConfig.selectedCurrency,
            priceConfigs: product.priceConfigs
          } as FileProductMultiCurrencyConfig
        } as FileProductInput : undefined)
      });
      setTillIsEditFileInput(true);
    }
  };
  const onProductClick = (product: TillProduct) => {
    const hasNoOptions = !product._options.length && !product.configuredOptionGroups.length;
    if (hasNoOptions) {
      addProductToInput(product);
    } else {
      addProductToTempProduct(product);
    }
  };
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const {
    data: additionalFees = []
  } = useGetAdditionalFees();
  const {
    data: fixDiscounts = []
  } = useGetFixDiscounts();
  const getMultiCurrencyConfig = () => {
    if (!company?.currencyConfigs) return null;
    const configs = company.currencyConfigs.map(c => ({
      code: c.code,
      rate: c.rate,
      isPrimaryCurrency: c.isPrimaryCurrency
    }));
    return {
      currencyConfigs: configs
    };
  };
  const getProductMultiCurrencyPriceConfig = (product: FileProductInput) => {
    if (!product.multiCurrencyConfig) return null;
    const matched = products.find(p => p._id === product.product);
    if (matched) {
      return {
        priceConfigs: matched.priceConfigs
      };
    } else {
      return {
        priceConfigs: product.multiCurrencyConfig.priceConfigs
      };
    }
  };
  const getOptionMultiCurrencyPriceConfig = (option: FileProductOptionInput) => {
    if (!option.multiCurrencyConfig) return null;
    const matched = productOptions.find(p => p._id === option.option);
    if (matched) {
      return {
        priceConfigs: matched.priceConfigs
      };
    } else {
      return {
        priceConfigs: option.multiCurrencyConfig.priceConfigs
      };
    }
  };
  const handleFileSelectedCurrencyChanged = (val: string) => {
    if (isCreatingFile) {
      if (!createFileInput) return;
      updateCreateFileInput(calculateFile({
        ...createFileInput,
        products: createFileInput.products.map(p => ({
          ...p,
          multiCurrencyConfig: {
            ...getProductMultiCurrencyPriceConfig(p),
            selectedCurrency: val
          },
          options: p.options.map(o => ({
            ...o,
            multiCurrencyConfig: {
              ...getOptionMultiCurrencyPriceConfig(o),
              selectedCurrency: val
            }
          }))
        })),
        cancelledProducts: createFileInput.cancelledProducts.map(p => ({
          ...p,
          multiCurrencyConfig: {
            ...getProductMultiCurrencyPriceConfig(p),
            selectedCurrency: val
          },
          options: p.options.map(o => ({
            ...o,
            multiCurrencyConfig: {
              ...getOptionMultiCurrencyPriceConfig(o),
              selectedCurrency: val
            }
          }))
        })),
        multiCurrencyConfig: {
          ...getMultiCurrencyConfig(),
          selectedCurrency: val
        }
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
    if (isViewingFile) {
      if (!updateFileInput) return;
      updateUpdateFileInput(calculateFile({
        ...updateFileInput,
        products: updateFileInput.products.map(p => ({
          ...p,
          multiCurrencyConfig: {
            ...getProductMultiCurrencyPriceConfig(p),
            selectedCurrency: val
          },
          options: p.options.map(o => ({
            ...o,
            multiCurrencyConfig: {
              ...getOptionMultiCurrencyPriceConfig(o),
              selectedCurrency: val
            }
          }))
        })),
        cancelledProducts: updateFileInput.cancelledProducts.map(p => ({
          ...p,
          multiCurrencyConfig: {
            ...getProductMultiCurrencyPriceConfig(p),
            selectedCurrency: val
          },
          options: p.options.map(o => ({
            ...o,
            multiCurrencyConfig: {
              ...getOptionMultiCurrencyPriceConfig(o),
              selectedCurrency: val
            }
          }))
        })),
        multiCurrencyConfig: {
          ...getMultiCurrencyConfig(),
          selectedCurrency: val
        }
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
    void setTillIsEditingFileInput(true);
  };
  const getFilteredAdditionalFees = () => {
    const discounts: Record<string, GetAdditionalFeesResults[0]> = {};
    const agent = isCreatingFile ? createFileInput?.agent : updateFileInput?.agent;
    const client = isCreatingFile ? createFileInput?.client : updateFileInput?.client;
    for (const discount of additionalFees) {
      if (discount.isDeactivated) continue;
      for (const conf of discount.configs) {
        const duplicate = discounts[`${discount._id}`];
        if (duplicate) continue;
        switch (conf.type) {
          case FIX_DISCOUNT_TYPE.CATEGORIES:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.PRODUCTS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.PRODUCT_TYPES:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.OPTIONS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.AGENTS:
            {
              if (!agent) break;
              const matchedAgent = conf.agents.includes(agent);
              if (matchedAgent) discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.CLIENTS:
            {
              if (!client) break;
              const matchedClient = conf.clients.includes(client);
              if (matchedClient) discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.ORDERS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
        }
      }
    }
    return Object.values(discounts) as unknown as GetAdditionalFeesResults;
  };
  const filteredAdditionalFees: GetAdditionalFeesResults = getFilteredAdditionalFees();
  const handleAdditionalFeeChange = (additionalFee: AdditionalFee) => {
    if (isCreatingFile) {
      if (!createFileInput) return;
      if (additionalFee) {
        const selectedAdditionalFee = {
          type: additionalFee ? additionalFee.configs[0].discountType : DISCOUNT_TYPE.PERCENTAGE,
          value: additionalFee ? additionalFee.configs[0].discountValue : 0,
          additionalFee: additionalFee._id
        };
        const discountedFile = !isOrderAdditionalFee(additionalFee) ? applyNonOrderAdditionalFeeToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: additionalFee as unknown as AdditionalFeeLike
        }) : applyAdditionalFeeToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: selectedAdditionalFee as unknown as AdditionalFeeInput
        });
        updateCreateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      } else {
        const selectedAdditionalFee = {
          type: DISCOUNT_TYPE.PERCENTAGE,
          value: 0
        };
        const discountedFile = applyAdditionalFeeToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: selectedAdditionalFee as unknown as AdditionalFeeInput
        });
        updateCreateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      }
    }
    if (isViewingFile) {
      if (!updateFileInput) return;
      if (additionalFee) {
        const selectedAdditionalFee = {
          type: additionalFee.configs[0].discountType,
          value: additionalFee.configs[0].discountValue,
          additionalFee: additionalFee._id
        };
        const discountedFile = !isOrderAdditionalFee(additionalFee) ? applyNonOrderAdditionalFeeToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: additionalFee as unknown as AdditionalFeeLike
        }) : applyAdditionalFeeToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: selectedAdditionalFee as unknown as AdditionalFeeInput
        });
        updateUpdateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      } else {
        const selectedAdditionalFee = {
          type: DISCOUNT_TYPE.PERCENTAGE,
          value: 0
        };
        const discountedFile = applyAdditionalFeeToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          additionalFee: selectedAdditionalFee as unknown as AdditionalFeeInput
        });
        updateUpdateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      }
    }
    setTillIsEditFileInput(true);
  };
  const getFilteredFixDiscounts = () => {
    const discounts: Record<string, GetFixDiscountsResults[0]> = {};
    const agent = isCreatingFile ? createFileInput?.agent : updateFileInput?.agent;
    const client = isCreatingFile ? createFileInput?.client : updateFileInput?.client;
    for (const discount of fixDiscounts) {
      if (discount.isDeactivated) continue;
      for (const conf of discount.configs) {
        const duplicate = discounts[`${discount._id}`];
        if (duplicate) continue;
        switch (conf.type) {
          case FIX_DISCOUNT_TYPE.CATEGORIES:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.PRODUCTS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.PRODUCT_TYPES:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.OPTIONS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.AGENTS:
            {
              if (!agent) break;
              const matchedAgent = conf.agents.includes(agent);
              if (matchedAgent) discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.CLIENTS:
            {
              if (!client) break;
              const matchedClient = conf.clients.includes(client);
              if (matchedClient) discounts[`${discount._id}`] = discount;
              break;
            }
          case FIX_DISCOUNT_TYPE.ORDERS:
            {
              discounts[`${discount._id}`] = discount;
              break;
            }
        }
      }
    }
    return Object.values(discounts) as unknown as GetFixDiscountsResults;
  };
  const filteredFixDiscounts: GetFixDiscountsResults = getFilteredFixDiscounts();
  const handleFixDiscountChange = (fixDiscount: FixDiscount) => {
    if (isCreatingFile) {
      if (!createFileInput) return;
      if (fixDiscount) {
        const selectedFixDiscount = {
          type: fixDiscount ? fixDiscount.configs[0].discountType : DISCOUNT_TYPE.PERCENTAGE,
          value: fixDiscount ? fixDiscount.configs[0].discountValue : 0,
          fixDiscount: fixDiscount._id
        };
        const discountedFile = !isOrderFixDiscount(fixDiscount) ? applyNonOrderFixDiscountToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: fixDiscount as unknown as FixDiscountLike
        }) : applyFixDiscountToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: selectedFixDiscount as unknown as FixDiscountInput
        });
        updateCreateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      } else {
        const selectedFixDiscount = {
          type: DISCOUNT_TYPE.PERCENTAGE,
          value: 0
        };
        const discountedFile = applyFixDiscountToFile({
          file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: selectedFixDiscount as unknown as FixDiscountInput
        });
        updateCreateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      }
    }
    if (isViewingFile) {
      if (!updateFileInput) return;
      if (fixDiscount) {
        const selectedFixDiscount = {
          type: fixDiscount.configs[0].discountType,
          value: fixDiscount.configs[0].discountValue,
          fixDiscount: fixDiscount._id
        };
        const discountedFile = !isOrderFixDiscount(fixDiscount) ? applyNonOrderFixDiscountToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: fixDiscount as unknown as FixDiscountLike
        }) : applyFixDiscountToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: selectedFixDiscount as unknown as FixDiscountInput
        });
        updateUpdateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      } else {
        const selectedFixDiscount = {
          type: DISCOUNT_TYPE.PERCENTAGE,
          value: 0
        };
        const discountedFile = applyFixDiscountToFile({
          file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
          fixDiscount: selectedFixDiscount as unknown as FixDiscountInput
        });
        updateUpdateFileInput({
          products: [...(discountedFile.products as unknown as FileProductInput[])]
        });
      }
    }
    setTillIsEditFileInput(true);
  };
  const createFileInputItems = useMemo<TableItem[]>(() => createFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [createFileInputSummary.products, productOptions, products]);
  const updateFileInputItems = useMemo<TableItem[]>(() => updateFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [updateFileInputSummary.products, productOptions, products]);
  const fileItemsDisplay = useMemo<TableItemDisplay>(() => ({
    prev: tillItemsSummary.prev.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt
    })),
    add: tillItemsSummary.add.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    remove: tillItemsSummary.remove.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    }))
  }), [productOptions, products, tillItemsSummary.add.products, tillItemsSummary.prev.products, tillItemsSummary.remove.products]);
  const getProductPrice = (product: GetTillProductCategoriesResults[0]["_products"][0]) => {
    if (isCreatingFile) {
      const config = createFileInput?.multiCurrencyConfig;
      return $dTillProductPrice(product, config);
    }
    if (isViewingFile) {
      const config = updateFileInput?.multiCurrencyConfig;
      return $dTillProductPrice(product, config);
    }
    return $dTillProductPrice(product, undefined);
  };
  return <>
			<TempProductModal data-sentry-element="TempProductModal" data-sentry-source-file="index.tsx" />

			<Dialog open fullWidth maxWidth={false} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
				<Stack sx={{
        height: "100dvh",
        overflow: "auto"
      }} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
					<AppBar color="inherit" sx={{
          position: "relative",
          width: "100vw"
        }} data-sentry-element="AppBar" data-sentry-source-file="index.tsx">
						<Grid container spacing={3} p={1} sx={{
            alignItems: "center"
          }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
							<Grid xs={4} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
								<div>
									<Typography variant="h6" component="div" sx={{
                  ml: 1
                }} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
										Categories
									</Typography>
								</div>
							</Grid>
							<Grid xs={4} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
								<div>
									<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
										<Grid xs={6} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
											<Typography variant="h6" component="div" sx={{
                      ml: 1
                    }} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
												Products
											</Typography>
										</Grid>
										<Grid xs={5} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
											<Autocomplete sx={{
                      width: 1
                    }} onChange={(_e, val) => handleAdditionalFeeChange(val as AdditionalFee)} getOptionLabel={opt => opt.name} options={filteredAdditionalFees} renderInput={params => <TextField {...params} label="Additional Fees" size="small" />} data-sentry-element="Autocomplete" data-sentry-source-file="index.tsx" />
										</Grid>
									</Grid>
								</div>
							</Grid>
							<Grid xs={4} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
								<div>
									<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
										<Grid xs={5} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
											<Autocomplete sx={{
                      width: 1
                    }} onChange={(_e, val) => handleFixDiscountChange(val as unknown as FixDiscount)} getOptionLabel={opt => opt.name} options={filteredFixDiscounts} renderInput={params => <TextField {...params} label="Fix Discounts" size="small" />} data-sentry-element="Autocomplete" data-sentry-source-file="index.tsx" />
										</Grid>
										<Grid xs={5} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
											{company?.isMultiCurrencyEnabled && company.currencyConfigs ? <FormControl fullWidth>
													<InputLabel id="selected-currency-label">
														Selected Currency
													</InputLabel>
													<Select labelId="selected-currency-label" id="selected-currency-label" size="small" defaultValue={createFileInput ? company.currencyConfigs.find(c => c.isPrimaryCurrency)?.code : updateFileInput ? updateFileInput.multiCurrencyConfig?.selectedCurrency ? updateFileInput.multiCurrencyConfig.selectedCurrency : DEFAULT_CURRENCY_CODE : DEFAULT_CURRENCY_CODE} onChange={e => handleFileSelectedCurrencyChanged(e.target.value)}>
														{company.currencyConfigs.map(c => <MenuItem key={c.code} value={c.code}>
																{c.code} ({currencyMap[c.code]})
															</MenuItem>)}
													</Select>
												</FormControl> : <FormControl fullWidth>
													<InputLabel id="selected-currency-label">
														Selected Currency
													</InputLabel>
													<Select labelId="selected-currency-label" id="selected-currency-label" size="small" value={DEFAULT_CURRENCY_CODE} label="Payment Stage" disabled>
														<MenuItem key={DEFAULT_CURRENCY_CODE} value={DEFAULT_CURRENCY_CODE}>
															USD
														</MenuItem>
													</Select>
												</FormControl>}
										</Grid>
										<Grid xs={2} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
											<IconButton disabled={isEditingFileInput} onClick={handleCancel} data-sentry-element="IconButton" data-sentry-source-file="index.tsx">
												<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="index.tsx">
													<XIcon data-sentry-element="XIcon" data-sentry-source-file="index.tsx" />
												</SvgIcon>
											</IconButton>
										</Grid>
									</Grid>
								</div>
							</Grid>
						</Grid>
					</AppBar>
					<Grid container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
						<Grid xs={12} md={4} sx={theme => ({
            borderRight: `1px solid ${theme.palette.divider}`,
            minHeight: 100
          })} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
							<div>
								<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
									{categories.filter(c => c._products.length > 0).map(category => <Grid key={category._id} xs={12} md={4}>
												<Card variant="outlined" sx={[{
                    cursor: "pointer",
                    ":hover": {
                      boxShadow: t => t.shadows[8]
                    }
                  }, category._id === selectedCategoryId && {
                    boxShadow: t => t.shadows[8],
                    borderColor: "primary.main",
                    backgroundColor: "primary.main",
                    color: t => t.palette.primary.contrastText,
                    ".MuiCardHeader-subheader": {
                      color: t => t.palette.primary.contrastText
                    }
                  }]} onClick={() => setSelectCategoryId(category._id)}>
													<CardHeader title={<Typography gutterBottom variant="h6" sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "3.6em",
                      lineHeight: "1.2em"
                    }}>
																{category.name}
															</Typography>} subheader={`${category._products.length} products`} />
													<CardActions />
												</Card>
											</Grid>)}
								</Grid>
							</div>
						</Grid>
						<Grid xs={12} md={4} sx={theme => ({
            borderRight: `1px solid ${theme.palette.divider}`,
            minHeight: 100
          })} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
							<div>
								<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
									{!!selectedCategory && selectedCategory._products.map(product => <Grid key={product._id} xs={12} md={4}>
												<Card variant="outlined" sx={[{
                    cursor: "pointer",
                    ":hover": {
                      boxShadow: t => t.shadows[8]
                    }
                  }, product._id === selectedProductId && {
                    boxShadow: t => t.shadows[8],
                    borderColor: "secondary.main",
                    backgroundColor: "secondary.main",
                    color: t => t.palette.primary.contrastText,
                    ".MuiCardHeader-subheader": {
                      color: t => t.palette.primary.contrastText
                    }
                  }]} onClick={() => onProductClick(product)}>
													<CardHeader title={<Typography gutterBottom variant="h6" sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "3.6em",
                      lineHeight: "1.2em"
                    }}>
																{product.name}
															</Typography>} subheader={getProductPrice(product)} />
													<CardActions />
												</Card>
											</Grid>)}
									{!!selectedCategory && !selectedCategory._products.length && <Grid xs={12} width="100%" textAlign="center">
											<Box alt="Not found" component="img" src="/assets/errors/error-404.png" sx={{
                    height: "auto",
                    maxWidth: "100%",
                    width: "200px",
                    mx: "auto",
                    mb: 2
                  }} />
											<Typography variant="h6">
												This category is empty
											</Typography>
										</Grid>}
								</Grid>
							</div>
						</Grid>
						<Grid xs={12} md={4} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
							{isCreatingFile && <ProductsTableSummary items={createFileInputItems} itemsDisplay={fileItemsDisplay} subtotal={createFileInputSummary.subtotal} additionalFee={createFileInputSummary.additionalFeeAmount} fixDiscount={createFileInputSummary.fixDiscountAmount} grandTotal={createFileInputSummary.grossAmount} onRowClick={item => {
              const inputItem = createFileInput!.products.find(p => p._id === item.id);
              if (inputItem) updateTempProduct(inputItem);
            }} isAddProduct={true} />}
							{isViewingFile && <ProductsTableSummary items={updateFileInputItems} itemsDisplay={fileItemsDisplay} subtotal={updateFileInputSummary.subtotal} additionalFee={updateFileInputSummary.additionalFeeAmount} fixDiscount={updateFileInputSummary.fixDiscountAmount} grandTotal={updateFileInputSummary.grossAmount} onRowClick={item => {
              const inputItem = updateFileInput!.products.find(p => p._id === item.id);
              if (inputItem) updateTempProduct(inputItem);
            }} isAddProduct={true} />}
						</Grid>
					</Grid>
					<Box sx={{
          position: "absolute",
          bottom: 16,
          right: 16
        }} gap={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
						<Button variant="contained" sx={{
            mx: 1
          }} disabled={!isEditingFileInput} onClick={handleCancel} data-sentry-element="Button" data-sentry-source-file="index.tsx">
							Cancel
						</Button>
						<Button variant="contained" sx={{
            mx: 1
          }} disabled={!isEditingFileInput} onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="index.tsx">
							Save
						</Button>
					</Box>
				</Stack>
			</Dialog>
		</>;
};